import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Cookie, ShieldCheck, Lock, Info, ExternalLink 
} from "lucide-react";

export default function CookiesPage() {
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden pb-20">
      
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[70%] bg-blue-200/30 blur-3xl rounded-full" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[60%] bg-sky-200/20 blur-3xl rounded-full" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 mb-8 group transition-all"
        >
          <div className="size-8 rounded-lg bg-white shadow-sm border border-blue-100 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
            <ArrowLeft className="size-4" />
          </div>
          Voltar para o Início
        </Link>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#33CCFF]/10 to-[#1A66CC]/5 rounded-[2.5rem] blur-2xl opacity-50" />
          
          <div className="relative bg-white/60 backdrop-blur-3xl rounded-[2rem] border border-white/80 p-8 lg:p-12 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.15),inset_0_1px_1px_rgba(255,255,255,1)]">
            
            <header className="flex flex-col sm:flex-row sm:items-center gap-5 mb-12 pb-8 border-b border-blue-100/50">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center text-white shadow-lg shadow-blue-500/30">
                <Cookie className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1">Cookies</h1>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Uso de Tecnologias de Armazenamento
                </p>
              </div>
            </header>

            <div className="prose prose-slate max-w-none space-y-10">
              <section className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100/50 leading-relaxed text-sm text-slate-600">
                O DataGrid XML utiliza cookies e tecnologias similares para garantir a segurança da sua sessão e o processamento correto dos seus dados fiscais. <strong>Não utilizamos cookies para publicidade direcionada.</strong>
              </section>

              <div className="space-y-6">
                <h2 className="text-xl font-black text-slate-900 m-0 flex items-center gap-3">
                  <ShieldCheck className="size-5 text-blue-600" />
                  Cookies Essenciais
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-4 font-black text-slate-900">Nome</th>
                        <th className="text-left py-4 font-black text-slate-900">Finalidade</th>
                        <th className="text-left py-4 font-black text-slate-900">Duração</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-600">
                      <tr>
                        <td className="py-4 font-mono text-xs">sb-access-token</td>
                        <td className="py-4">Mantém a autenticação segura do seu usuário via Supabase.</td>
                        <td className="py-4 italic">Sessão</td>
                      </tr>
                      <tr>
                        <td className="py-4 font-mono text-xs">theme-preference</td>
                        <td className="py-4">Armazena sua preferência de modo claro ou escuro.</td>
                        <td className="py-4 italic">1 ano</td>
                      </tr>
                      <tr>
                        <td className="py-4 font-mono text-xs">cookie-consent</td>
                        <td className="py-4">Lembra que você aceitou os termos desta política.</td>
                        <td className="py-4 italic">6 meses</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <section className="space-y-6">
                <h2 className="text-xl font-black text-slate-900 m-0 flex items-center gap-3">
                  <Lock className="size-5 text-blue-600" />
                  Local Storage
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Utilizamos o <strong>Local Storage</strong> do seu navegador para cache temporário de configurações da interface e para o funcionamento do Web Worker que processa os XMLs localmente. Estes dados nunca são enviados ao servidor.
                </p>
              </section>

              <section className="bg-slate-900 p-8 rounded-3xl text-white relative overflow-hidden">
                <div className="flex items-start gap-4">
                  <div className="size-10 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                    <Info className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black mb-2">Como gerenciar?</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4">
                      Você pode desativar os cookies nas configurações do seu navegador a qualquer momento. No entanto, lembre-se que isso impedirá o login e o uso da plataforma.
                    </p>
                    <a href="https://www.aboutcookies.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-black text-blue-400 hover:underline uppercase tracking-widest">
                      Saiba mais sobre cookies <ExternalLink className="size-3" />
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
